'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, Copy, Check } from 'lucide-react';

import { useRouter } from 'next/navigation';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

const DEMO_EMAIL = 'sutton.builders@westsuffolkbiz.co.uk';
const DEMO_PASSWORD = 'Suffolk2026!';

export default function LoginForm({ onSwitchToRegister }: { onSwitchToRegister: () => void }) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedPass, setCopiedPass] = useState(false);
  const router = useRouter();

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    formState: { errors },
  } = useForm<LoginFormData>({ defaultValues: { rememberMe: false } });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    // Backend integration point: POST /api/auth/login
    await new Promise((r) => setTimeout(r, 1200));
    if (data.email === DEMO_EMAIL && data.password === DEMO_PASSWORD) {
      toast.success('Welcome back, Sutton Builders!');
      router.push('/business-analytics-dashboard');
    } else {
      setError('email', {
        message: 'Invalid credentials — use the demo accounts below to sign in',
      });
    }
    setIsLoading(false);
  };

  const handleCopy = (value: string, field: 'email' | 'pass') => {
    if (field === 'email') {
      setValue('email', value);
      setCopiedEmail(true);
      setTimeout(() => setCopiedEmail(false), 2000);
    } else {
      setValue('password', value);
      setCopiedPass(true);
      setTimeout(() => setCopiedPass(false), 2000);
    }
  };

  return (
    <div>
      <h2 className="text-xl font-bold text-foreground mb-1">Sign in to your account</h2>
      <p className="text-sm text-muted-foreground mb-6">
        Access your business dashboard and listing management.
      </p>

      {/* Demo credentials box */}
      <div className="bg-primary/5 border border-primary/20 rounded p-4 mb-6">
        <p className="text-xs font-bold text-primary mb-2">Demo Credentials</p>
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div>
              <span className="text-xs text-muted-foreground">Email: </span>
              <span className="text-xs font-mono text-foreground">{DEMO_EMAIL}</span>
            </div>
            <button
              type="button"
              onClick={() => handleCopy(DEMO_EMAIL, 'email')}
              className="text-muted-foreground hover:text-primary transition-colors p-1 rounded"
              aria-label="Copy email and fill form"
            >
              {copiedEmail ? <Check size={13} className="text-positive" /> : <Copy size={13} />}
            </button>
          </div>
          <div className="flex items-center justify-between gap-2">
            <div>
              <span className="text-xs text-muted-foreground">Password: </span>
              <span className="text-xs font-mono text-foreground">{DEMO_PASSWORD}</span>
            </div>
            <button
              type="button"
              onClick={() => handleCopy(DEMO_PASSWORD, 'pass')}
              className="text-muted-foreground hover:text-primary transition-colors p-1 rounded"
              aria-label="Copy password and fill form"
            >
              {copiedPass ? <Check size={13} className="text-positive" /> : <Copy size={13} />}
            </button>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="mb-4">
          <label htmlFor="login-email" className="block text-sm font-semibold text-foreground mb-1.5">
            Email address
          </label>
          <input
            id="login-email"
            type="email"
            autoComplete="email"
            className={`input-field ${errors.email ? 'border-negative' : ''}`}
            placeholder="you@yourbusiness.co.uk"
            {...register('email', {
              required: 'Email address is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
            })}
          />
          {errors.email && (
            <p className="text-negative text-xs mt-1.5 font-semibold">{errors.email.message}</p>
          )}
        </div>

        <div className="mb-4">
          <div className="flex items-center justify-between mb-1.5">
            <label htmlFor="login-password" className="text-sm font-semibold text-foreground">
              Password
            </label>
            <button type="button" className="text-xs text-primary hover:underline font-semibold">
              Forgot password?
            </button>
          </div>
          <div className="relative">
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              className={`input-field pr-10 ${errors.password ? 'border-negative' : ''}`}
              placeholder="Your password"
              {...register('password', { required: 'Password is required' })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && (
            <p className="text-negative text-xs mt-1.5 font-semibold">{errors.password.message}</p>
          )}
        </div>

        <div className="flex items-center gap-2 mb-6">
          <input
            id="remember-me"
            type="checkbox"
            className="accent-primary w-4 h-4"
            {...register('rememberMe')}
          />
          <label htmlFor="remember-me" className="text-sm text-muted-foreground cursor-pointer">
            Keep me signed in for 30 days
          </label>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full justify-center text-sm py-3"
          style={{ minWidth: 200 }}
        >
          {isLoading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Signing in...
            </>
          ) : (
            'Sign In to Dashboard'
          )}
        </button>
      </form>

      <p className="text-sm text-muted-foreground text-center mt-5">
        Don&apos;t have an account?{' '}
        <button onClick={onSwitchToRegister} className="text-primary font-semibold hover:underline">
          Register your business
        </button>
      </p>

      <p className="text-xs text-muted-foreground text-center mt-4 leading-relaxed">
        By signing in you agree to our{' '}
        <span className="text-primary cursor-pointer hover:underline">Terms of Use</span>
        {' '}and{' '}
        <span className="text-primary cursor-pointer hover:underline">Privacy Policy</span>.
        <br />
        A service of West Suffolk Council.
      </p>
    </div>
  );
}